import React from 'react'
import dayjs from 'dayjs'
import { Order } from '../../Types/Order'
import { OrderUtil } from '../../Utils/OrderUtil'
import { FilterStatus, LocalSettings } from '../../Types/Enums'
import { Docket } from '../Docket/Docket'
import { NotificationDocket } from '../Docket/NotificationDocket'
import { observer } from 'mobx-react'

interface OrdersListProps {
  filteredOrders: Order[]
  config: any
  localSettings: LocalSettings | null
  printerLoading: string
  printOrder: (order: Order) => Promise<void>
  updateOrderStatus: (order: Order) => Promise<void>
  confirmWeightAction: (order: Order) => void
  numberOfActiveOrdersRequiringConfirmation: number
  numberOfUpcomingOrdersRequiringConfirmation: number
  currentFilter: FilterStatus
  setFilter: (filter: FilterStatus) => void
  fetchOrders: () => void
  onEditOrder?: (order: Order) => void
}

const groupOrdersByDate = (orders: Order[]): Record<string, Order[]> => {
  return orders.reduce(
    (groups, order) => {
      const orderDayjs = dayjs(order.timeFrom)
      const today = dayjs().startOf('day')
      const tomorrow = today.add(1, 'day')

      let orderDate
      if (orderDayjs.isSame(today, 'day')) {
        orderDate = 'Today'
      } else if (orderDayjs.isSame(tomorrow, 'day')) {
        orderDate = 'Tomorrow'
      } else {
        orderDate = orderDayjs.format('ddd MMM DD')
      }

      if (!groups[orderDate]) {
        groups[orderDate] = []
      }
      groups[orderDate].push(order)
      return groups
    },
    {} as Record<string, Order[]>
  )
}

export const OrdersList: React.FC<OrdersListProps> = observer(
  ({
    filteredOrders,
    config,
    localSettings,
    printerLoading,
    printOrder,
    updateOrderStatus,
    confirmWeightAction,
    numberOfActiveOrdersRequiringConfirmation,
    numberOfUpcomingOrdersRequiringConfirmation,
    currentFilter,
    setFilter,
    fetchOrders,
    onEditOrder,
  }) => (
    <div className="orders">
      <section>
        <div className={`dockets order-dockets order-dockets-${currentFilter?.toLowerCase()}`}>
          <>
            {currentFilter === FilterStatus.Active &&
              numberOfUpcomingOrdersRequiringConfirmation > 0 && (
                <NotificationDocket
                  count={numberOfUpcomingOrdersRequiringConfirmation}
                  currentFilter={currentFilter}
                  onClick={() => setFilter(FilterStatus.Upcoming)}
                />
              )}
            {currentFilter === FilterStatus.Upcoming &&
              numberOfActiveOrdersRequiringConfirmation > 0 && (
                <NotificationDocket
                  count={numberOfActiveOrdersRequiringConfirmation}
                  currentFilter={currentFilter}
                  onClick={() => setFilter(FilterStatus.Active)}
                />
              )}
          </>
          {currentFilter === FilterStatus.Upcoming
            ? // First group orders by date
              Object.entries(groupOrdersByDate(filteredOrders)).map(([date, dateOrders]) => (
                <React.Fragment key={date}>
                  <h3 className="order-day-title">{date}</h3>
                  <div className="order-day-group">
                    {dateOrders.map((order, index) => (
                      <Docket
                        key={order.orderNo}
                        order={order}
                        config={config}
                        print={() => printOrder(order)}
                        printingEnabled={localSettings?.printingEnabled}
                        printerLoading={printerLoading}
                        buttonLabel={OrderUtil.buttonLabel(
                          order.status,
                          currentFilter,
                          order.hasDeferredPayment,
                          order.weightRequiresConfirmation,
                          config.features.invoicingDelay
                        )}
                        action={() => updateOrderStatus(order)}
                        position={index + 1}
                        confirmWeightAction={() => confirmWeightAction(order)}
                        fetchOrders={fetchOrders}
                        onEditOrder={onEditOrder ? () => onEditOrder(order) : undefined}
                      />
                    ))}
                  </div>
                </React.Fragment>
              ))
            : filteredOrders.map((order, index) => (
                <Docket
                  key={order.orderNo}
                  order={order}
                  config={config}
                  print={() => printOrder(order)}
                  printingEnabled={localSettings?.printingEnabled}
                  printerLoading={printerLoading}
                  buttonLabel={OrderUtil.buttonLabel(
                    order.status,
                    currentFilter,
                    order.hasDeferredPayment,
                    order.weightRequiresConfirmation,
                    config.features.invoicingDelay
                  )}
                  action={updateOrderStatus}
                  position={index + 1}
                  confirmWeightAction={() => confirmWeightAction(order)}
                  fetchOrders={fetchOrders}
                  onEditOrder={onEditOrder ? () => onEditOrder(order) : undefined}
                />
              ))}
        </div>
      </section>
    </div>
  )
)
